import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location, NgIf, NgClass } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { StepClickedEvent } from '../../../shared/components/stepper/stepper.component';
import { LocationService } from '../../../shared/services/location.service';
import { EorderWizardService } from '../../services/eorder-wizard.service';
import { EorderWizardStep } from '../../models/eorder-wizard-state';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { ErrorService } from '../../../shared/services/error.service';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { LegalDocumentService } from '../../../shared/services/legal-document.service';
import {
	SubmissionFeedbackComponent
} from '../../../shared/components/submission-feedback/submission-feedback.component';
import { SubmitDialogComponent } from '../../../shared/components/submit-dialog/submit-dialog.component';
import { DialogWarningComponent } from '../../../shared/components/dialog-warning/dialog-warning.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import {
	LegalDocumentAgreementComponent
} from '../../../shared/components/legal/legal-document-user-agreement/legal-document-agreement.component';

import { WizardPageContent } from './wizard-page-content';
import { WizardStepperComponent } from './wizard-stepper/wizard-stepper.component';

@Component({
	selector: 'copo-wizard',
	templateUrl: './wizard.component.html',
	styleUrl: './wizard.component.css',
	imports: [
		ScrollerDirective,
		NgIf,
		NgClass,
		SubmissionFeedbackComponent,
		LegalDocumentAgreementComponent,
		PageHeaderComponent,
		MatButtonModule,
		TranslateModule,
		MatIconModule,
		WizardStepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective
	],
})
export class WizardComponent implements OnInit, OnDestroy {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	pageTitle: string;
	pageTitleTranslationCode = 'eorder.order.creation';
	isLoading = true;
	isConfirmDisabled = true;
	notAgreedLegalDocumentIds: number[] = [];
	previousPage: string | undefined;
	pageContent: WizardPageContent | undefined;
	orderIdSubscription?: Subscription;
	confirmationPopupSub: Subscription | undefined;
	isSaveAsDraftBtnDisable = false;
	serviceCode = 'EORDR';
	legalDocumentLabel: string;
	readonly readOnlyUser?: boolean;

	constructor(
		readonly locationService: LocationService,
		readonly legalDocumentService: LegalDocumentService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		readonly location: Location,
		readonly eorderWizardService: EorderWizardService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		readonly errorService: ErrorService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
	) {
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		this.previousPage = this.eorderWizardService.actionToGoBack();
		if (!this.previousPage) {
			this.previousPage = this.eorderWizardService.actionRetrievePreviousPage();
		}
		this.readOnlyUser = this.userService.user.readOnly;
		this.legalDocumentLabel = this.translationService.getTR('settings.sales.framework');
	}

	ngOnInit(): void {
		this.isLoading = false;
		this.orderIdSubscription = this.eorderWizardService.orderIdObs.subscribe(orderId => {
			if (orderId) {
				this.isSaveAsDraftBtnDisable = true;
			}
		});
	}

	ngOnDestroy() {
		if (this.orderIdSubscription) {
			this.orderIdSubscription.unsubscribe();
		}
		if (this.confirmationPopupSub) {
			this.confirmationPopupSub.unsubscribe();
		}
		this.eorderWizardService.cleanCart();
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['/order/wizard'], { relativeTo: this.route });
		}
	}

	getCurrentStatus(): string {
		return this.router.url;
	}

	goToBackView() {
		if (this.eorderWizardService.getCurrentRoute().endsWith('wizard/orderInformation')) {
			if (this.previousPage !== undefined) {
				location.href = this.previousPage;
			} else if (this.eorderWizardService.getNavigationCount() > 0) {
				this.locationService.go(-this.eorderWizardService.getNavigationCount(), this.previousPage);
			} else {
				this.locationService.go(-1, this.previousPage);
			}
		} else {
			location.href = '/home';
		}
	}

	async goNextStep() {
		this.errorService.resetErrorMsg();
		if (!this.pageContent || this.pageContent.checkError() || this.pageContent.checkErrorForDraft()) {
			return;
		}
		await this.eorderWizardService.actionGoNextStep();
	}

	onUserAgreementCheck(filterObject:
	{ agreedToSalesConditions: boolean; notAgreedLegalDocumentIds: number[]; isLoading: boolean }) {
		this.notAgreedLegalDocumentIds = filterObject.notAgreedLegalDocumentIds;
		this.isConfirmDisabled = Boolean(!filterObject.agreedToSalesConditions);
	}

	onActivate(pageContent: WizardPageContent) {
		this.pageContent = pageContent;
		if (!this.scrollBar) {
			return;
		}
		const scrollPosition = this.eorderWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex];
		this.scrollBar.scrollTo({ top: scrollPosition });
	}

	onDeactivate() {
		if (!this.pageContent || !this.scrollBar) {
			return;
		}
		this.pageContent = undefined;
	}

	isButtonViewCardVisible() {
		return !this.isLoading && this.eorderWizardService.getState().stepIndex === EorderWizardStep.ORDERINFORMATION;
	}

	isButtonNextVisible() {
		return !this.isLoading
			&& this.eorderWizardService.getState().stepIndex === EorderWizardStep.PRODUCTMODIFICATION;
	}

	isBtnProceedOrderVisible() {
		return !this.isLoading && this.eorderWizardService.getState().stepIndex === EorderWizardStep.SUMMARY;
	}

	getProductCount() {
		return this.eorderWizardService.getProductCount();
	}

	isButtonDisabled() {
		return this.eorderWizardService.getStateEorderForm().getFormProducts().length <= 0;
	}

	isButtonSubmitVisible() {
		return !this.isLoading && this.eorderWizardService.getState().stepIndex === EorderWizardStep.SUMMARY;
	}

	evtSaveDraftOrder() {
		const customerOrderNumber = <string> this.eorderWizardService.getStateEorderForm()
			.getFormCustomerOrderNumber().value;
		if (!this.pageContent || this.pageContent.checkErrorForDraft() || customerOrderNumber === '') {
			return;
		}
		this.errorService.resetErrorMsg();
		const dialogRef = this.dialog.open(DialogWarningComponent, {
			data: {
				iconName: 'action_save',
				title: 'eorder.action.save.draft',
				contents: [
					this.translationService.getTR('eorder.save.draft.confirmation')
						.replace('[X]', `<b>${ customerOrderNumber }</b>`)
				],
				cancelButtonEnabled: true
			}
		});
		this.confirmationPopupSub = dialogRef.afterClosed()
			.subscribe(result => {
				if (result) {
					const sub = this.dialog.open<SubmitDialogComponent, undefined, number>(
						SubmitDialogComponent,
						{ panelClass: 'copo-offer-dialog', disableClose: true }
					);
					sub.componentInstance.progressMessage = this.translationService.getTR('progress.message');
					sub.componentInstance.waitMessage = this.translationService.getTR('wait.message');

					this.eorderWizardService.actionSaveCurrentOrder().catch((error: unknown) => {
						console.error('unable to save order', error);
						this.errorService.setErrorMsg(
							this.translationService.getTR('eorder.save.draft.error.message'),
							'quapp.error'
						);
					}).finally(() => {
						sub.close();
					});
				} else {
					this.openSnackBar(this.translationService.getTR('global.action.cancelled'));
				}
			});
	}

	async evtSubmitEorder() {
		const sub = this.dialog.open<SubmitDialogComponent, undefined, number>(
			SubmitDialogComponent,
			{ panelClass: 'copo-offer-dialog', disableClose: true }
		);
		this.errorService.resetErrorMsg();
		sub.componentInstance.progressMessage = this.translationService.getTR('progress.message');
		sub.componentInstance.waitMessage = this.translationService.getTR('wait.message');
		for (const notAgreedLegalDocumentId of this.notAgreedLegalDocumentIds) {
			await this.legalDocumentService.saveUserAgreement(notAgreedLegalDocumentId);
		}
		await this.eorderWizardService.actionSubmitOrder();
		sub.close();
	}

	private openSnackBar(message: string) {
		this.snackBar.open(message, undefined, { duration: 5000 });
	}
}
